import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  MapPin, Star, Globe, AlertTriangle, Lock, Loader2, ArrowRight,
  CheckCircle2, XCircle, BarChart3, Bot,
} from "lucide-react";
import { ScoreRing } from "@/components/report/ScoreRing";
import { getScoreColor, getScoreBg, getScoreLabel } from "@/components/report/reportHelpers";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SharedReportPage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/shared/${shareToken}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This shared report is not available.");
      } finally { setLoading(false); }
    };
    fetchReport();
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#3B5BDB]" />
        <p className="text-[#6B7280]">Loading shared report...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <Card className="bg-white border-[#E5E7EB] max-w-md">
        <CardContent className="p-8 text-center">
          <Lock className="w-12 h-12 text-[#DC2626] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#111827] mb-2">Report Unavailable</h2>
          <p className="text-[#6B7280] text-sm mb-6">{error}</p>
          <Button onClick={() => navigate("/audit")} className="bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white">
            Run Your Own Audit <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const { business = {}, scores = {}, issues = [], competitors = [], ai_search_simulation = [], is_paid, cache_expired } = data;
  const signals = scores.signals || [];
  const overall = scores.overall || 0;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#3B5BDB] rounded-lg flex items-center justify-center"><MapPin className="w-4 h-4 text-white" /></div>
            <span className="font-heading font-bold text-[#111827]">Rankify<span className="gradient-text">Local</span></span>
          </button>
          <div className="flex items-center gap-3">
            <Badge className="bg-[#EEF2FF] text-[#3B5BDB] border-[#3B5BDB]/20 text-xs">Shared Report</Badge>
            <Button size="sm" onClick={() => navigate("/audit")} className="bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white text-xs" data-testid="shared-run-audit-btn">
              Run Your Own Audit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="rounded-2xl overflow-hidden bg-white border border-[#E5E7EB] mb-8" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <div className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <ScoreRing score={overall} size={120} stroke={10} glow />
                <div className="flex-1">
                  <p className="font-mono text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Local SEO Audit Report</p>
                  <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#111827] mb-1" data-testid="shared-business-name">{business.name}</h1>
                  <p className="text-sm mb-3 text-[#6B7280]">{business.address}</p>
                  <div className="flex flex-wrap gap-4">
                    {business.rating && <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Star className="w-4 h-4 text-amber-500 fill-amber-500" />{business.rating} ({business.review_count || 0} reviews)</div>}
                    {business.phone && <span className="text-sm text-[#6B7280]">{business.phone}</span>}
                    {business.website && <a href={business.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-[#3B5BDB]"><Globe className="w-3.5 h-3.5" />Website</a>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <Badge className={`text-base px-5 py-1.5 font-semibold ${getScoreBg(overall)} text-white shadow-lg`}>{getScoreLabel(overall)}</Badge>
                  <div className="flex gap-4 mt-1">
                    {[{ label: "GBP", score: scores.gbp?.score }, { label: "Reviews", score: scores.reviews?.score }, { label: "AI Ready", score: scores.ai_readiness?.score }].map((s, i) => (
                      <div key={i} className="text-center">
                        <p className={`text-xl font-bold ${getScoreColor(s.score || 0)}`}>{s.score || 0}</p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-[#9CA3AF]">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Signal Scorecard */}
          {signals.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-50"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#111827]">Ranking Signal Scorecard</h2>
                  <p className="text-sm text-[#6B7280]">How this business ranks across key signals</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {signals.map((sig, i) => (
                  <Card key={i} className="bg-white border-[#E5E7EB]" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-lg ${sig.status === "active" ? "text-[#059669]" : sig.status === "at_risk" ? "text-[#D97706]" : "text-[#DC2626]"}`}>
                            {sig.status === "active" ? "\u2713" : sig.status === "at_risk" ? "\u26A0" : "\u2717"}
                          </span>
                          <span className="font-semibold text-sm text-[#111827]">{sig.name}</span>
                        </div>
                        <span className={`text-lg font-bold ${getScoreColor(sig.score)}`}>{sig.score}<span className="text-xs text-[#9CA3AF]">/100</span></span>
                      </div>
                      <div className="relative h-1.5 rounded-full overflow-hidden bg-[#E5E7EB]">
                        <div className={`absolute left-0 top-0 h-full rounded-full ${getScoreBg(sig.score)}`} style={{ width: `${sig.score}%` }} />
                      </div>
                      <p className="text-xs text-[#6B7280] mt-2">{sig.detail}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          <Separator className="bg-[#E5E7EB] my-8" />

          {/* Issues */}
          {issues.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-50"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#111827]">{issues.length} Issues Found</h2>
                  <p className="text-sm text-[#6B7280]">Problems affecting local search visibility</p>
                </div>
              </div>
              {["high", "medium", "low"].map((priority) => {
                const pIssues = issues.filter(i => i.priority === priority);
                if (!pIssues.length) return null;
                return (
                  <div key={priority} className="mb-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={priority === "high" ? "bg-red-50 text-red-500 border-red-200" : priority === "medium" ? "bg-amber-50 text-amber-500 border-amber-200" : "bg-emerald-50 text-emerald-500 border-emerald-200"}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
                      </Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {pIssues.map((issue, i) => (
                        <Card key={i} className={`bg-white border-[#E5E7EB] border-l-4 ${priority === "high" ? "border-l-red-500" : priority === "medium" ? "border-l-amber-500" : "border-l-emerald-500"}`}>
                          <CardContent className="p-4">
                            <p className="font-medium text-sm text-[#111827]">{issue.issue}</p>
                            <p className="text-xs mt-1 text-[#9CA3AF]">{issue.category}</p>
                            <p className="text-xs mt-2 text-[#6B7280]">{issue.impact}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          <Separator className="bg-[#E5E7EB] my-8" />

          {/* Blurred Premium Content */}
          <section className="mb-10">
            <div className="relative">
              <div className="max-h-[250px] overflow-hidden">
                <div className="space-y-4 opacity-60">
                  <h2 className="font-heading text-xl font-bold text-[#111827]">Personalized Recommendations</h2>
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="bg-white border-[#E5E7EB]">
                      <CardContent className="p-5">
                        <div className="h-4 w-3/4 rounded bg-[#E5E7EB]" />
                        <div className="h-3 w-1/2 rounded mt-2 bg-[#E5E7EB]" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              <div className="absolute inset-0 top-[80px]" style={{ backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }}>
                <div className="w-full h-full bg-white/60" />
              </div>
              <div className="relative -mt-24 flex justify-center pb-8 z-10">
                <div className="bg-white border border-[#D1D5DB] rounded-2xl p-8 text-center max-w-md mx-4" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                  <div className="w-12 h-12 bg-[#EEF2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-5 h-5 text-[#3B5BDB]" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-[#111827] mb-2">Want Your Own Report?</h3>
                  <p className="text-sm text-[#6B7280] mb-6">Run a free audit for your business and unlock personalized recommendations, a 90-day plan, and copy-paste templates.</p>
                  <Button onClick={() => navigate("/audit")} className="bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white font-semibold px-8 h-11 w-full" data-testid="shared-unlock-btn">
                    Run My Free Audit <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {cache_expired && (
            <Card className="bg-amber-50 border-amber-200 mb-8">
              <CardContent className="p-4 text-center text-sm text-[#D97706]">
                This report's data may be outdated. The owner can re-run the audit to refresh it.
              </CardContent>
            </Card>
          )}
        </motion.div>
      </main>

      <footer className="border-t border-[#E5E7EB] py-6 bg-white">
        <p className="text-center text-xs text-[#9CA3AF]">Powered by RankifyLocal — Local SEO Audit Platform</p>
      </footer>
    </div>
  );
}
